import React from "react";
import { useIsomorphicLayoutEffect } from "react-use";
import { useMotionValueEvent, useScroll } from "framer-motion";
import { debounce } from "~/features/app/utils";
type ScrollState = "START" | "MIDDLE" | "END";
type Props = {
  ref: React.RefObject<HTMLDivElement | null>;
  activeSectionId: string;
};
export function useScrollableAnchors(props: Props) {
  const {
    ref,
    activeSectionId
  } = props;
  const [isScrollable, setIsScrollable] = React.useState(false);
  const [scrollState, setScrollState] = React.useState<ScrollState>("START");
  const {
    scrollXProgress
  } = useScroll({
    container: ref
  });
  useIsomorphicLayoutEffect(() => {
    const container = ref.current;
    const element = document.getElementById(`anchor-${activeSectionId}`);
    if (!container || !element) {
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const activeTabRect = element.getBoundingClientRect();
    let newScrollLeft = 0;
    let shouldScroll = false;
    if (activeTabRect.left < containerRect.left) {
      newScrollLeft = container.scrollLeft - (containerRect.left - activeTabRect.left);
      shouldScroll = true;
    } else if (activeTabRect.right > containerRect.right) {
      newScrollLeft = container.scrollLeft + (activeTabRect.right - containerRect.right);
      shouldScroll = true;
    }
    if (shouldScroll) {
      container.scrollTo({
        left: newScrollLeft,
        behavior: "smooth"
      });
    }
  }, [activeSectionId]);
  React.useEffect(() => {
    function onResize() {
      if (!ref?.current) {
        return;
      }
      setIsScrollable(ref.current.scrollWidth > ref.current.clientWidth);
    }
    onResize();
    const debouncedOnResize = debounce(onResize);
    window.addEventListener("resize", debouncedOnResize);
    return () => {
      window.removeEventListener("resize", debouncedOnResize);
    };
  }, [ref]);
  useMotionValueEvent(scrollXProgress, "change", v => {
    if (v === 0) {
      setScrollState("START");
    } else if (v >= 1) {
      setScrollState("END");
    } else {
      setScrollState("MIDDLE");
    }
  });
  return {
    scrollState,
    isScrollable
  };
}